import * as React from 'react';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchInvites, revokeInvite } from '@/services/workspace';
import { toast } from 'sonner';
import { getFullName } from '@/lib/string';
import { useTranslation } from 'react-i18next';
import { legacyCreateColumnHelper } from '@tanstack/react-table/legacy';
import { Trash } from 'lucide-react';
import { DataTable } from '@/components/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { isRoleAtLeast, isTeam } from '@/lib/role';
import { errorHandler } from '@/lib/utils';
const columnHelper = legacyCreateColumnHelper();
export const WorkspaceInvitesAdminTable = ({ workspace }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [rowSelection, setRowSelection] = React.useState({});
    const { data: invites = [], isPending } = useQuery({
        queryKey: ['workspace-invites', workspace.publicId],
        queryFn: () => fetchInvites(workspace.publicId),
        enabled: isTeam(workspace) && isRoleAtLeast(workspace.role, 'ADMIN'),
    });
    const { mutate } = useMutation({
        mutationFn: revokeInvite,
        onSuccess: (data) => {
            toast.success(data.message);
            queryClient.invalidateQueries({
                queryKey: ['workspace-invites', workspace.publicId],
            });
        },
        onError: errorHandler,
    });
    const column = React.useMemo(() => [
        columnHelper.accessor('invitedUser', {
            header: () => t('user'),
            cell: ({ row: { original: invite } }) => (<div className="space-y-2">
                        <p className="font-medium">{getFullName(invite.invitedUser)}</p>
                        <p className="text-xs text-muted-foreground">{invite.invitedUser.email}</p>
                    </div>),
            footer: () => t('user'),
        }),
        columnHelper.accessor('role', {
            header: () => t('role'),
            cell: (info) => t(info.getValue().toLowerCase()),
            footer: () => t('role'),
        }),
        columnHelper.accessor('createdAt', {
            header: () => t('invited_at'),
            cell: ({ getValue }) => new Date(getValue()).toLocaleDateString(),
            footer: () => t('invited_at'),
        }),
        columnHelper.accessor('publicId', {
            header: () => t('actions'),
            cell: ({ row: { original: invite } }) => (<Button variant="ghost" size="icon" onClick={() => mutate({ workspacePublicId: workspace.publicId, invitePublicId: invite.publicId })}>
                        <Trash />
                        {t('revoke')}
                    </Button>),
            footer: () => t('actions'),
        }),
    ], [mutate, t, workspace.publicId]);
    if (isRoleAtLeast(workspace.role, 'ADMIN')) {
        return (<Card className="w-full">
                <CardHeader>
                    <CardTitle>{t('pending_invites')}</CardTitle>
                </CardHeader>

                <CardContent>
                    <DataTable data={invites} columns={column} rowSelection={rowSelection} onRowSelectionChange={setRowSelection} loading={isPending}/>
                </CardContent>
            </Card>);
    }
    return null;
};
WorkspaceInvitesAdminTable.displayName = 'WorkspaceInvitesAdminTable';
